import React from "react";

// SEVERITY matches WUA's MsrcSeverity values; CATEGORY matches the names WUA
// puts on update.Categories[].Name. Keep these in sync with what the agent
// reports in agent/patches.py.
export const SEVERITY_OPTIONS = ["Critical", "Important", "Moderate", "Low", "Unspecified"];
export const CATEGORY_OPTIONS = [
  "Security Updates",
  "Critical Updates",
  "Definition Updates",
  "Cumulative Updates",
  "Feature Updates",
  "Drivers",
  "Updates",
  "Service Packs",
];

export function PolicyChipPicker({
  label,
  options,
  selected,
  onChange,
  color,
}: {
  label: string;
  options: string[];
  selected: string[];
  onChange: (v: string[]) => void;
  color: string;
}) {
  const toggle = (opt: string) => {
    if (selected.includes(opt)) onChange(selected.filter((x) => x !== opt));
    else onChange([...selected, opt]);
  };
  return (
    <div className="mb-4">
      <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const on = selected.includes(opt);
          return (
            <button
              key={opt}
              onClick={() => toggle(opt)}
              type="button"
              className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                on
                  ? color
                  : "bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40"
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function KbListEditor({
  label,
  kbs,
  onChange,
  color,
}: {
  label: string;
  kbs: string[];
  onChange: (v: string[]) => void;
  color: string;
}) {
  const [draft, setDraft] = React.useState("");
  const add = () => {
    const v = draft.trim().toUpperCase();
    if (!v) return;
    const normalized = v.startsWith("KB") ? v : `KB${v}`;
    if (!kbs.includes(normalized)) onChange([...kbs, normalized]);
    setDraft("");
  };
  return (
    <div className="mb-4">
      <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">{label}</div>
      <div className="flex items-center gap-2 mb-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder="KB5034123 or 5034123"
          className="border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm w-48"
        />
        <button
          type="button"
          onClick={add}
          className="text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 px-3 py-1.5 rounded-lg"
        >
          Add
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {kbs.length === 0 && <span className="text-xs text-gray-400 dark:text-gray-500">(none)</span>}
        {kbs.map((kb) => (
          <span
            key={kb}
            className={`text-xs px-2 py-1 rounded-full border inline-flex items-center gap-1 ${color}`}
          >
            {kb}
            <button
              type="button"
              onClick={() => onChange(kbs.filter((x) => x !== kb))}
              className="hover:text-red-700 ml-1"
            >
              ×
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}
